"use client";
import { useRef, useEffect } from "react";
import Link from "next/link";
import { ChevronLeft, LayoutGrid, X } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import FallbackImage from "@/app/components/elements/FallbackImage";
import Search from "@/app/components/elements/forms/search/Search";
import { MenuItem } from "@/app/lib/features/menus/menus.types";
import { cn } from "@/app/utils/cn";
import { trackInteraction } from "@/app/lib/tracking/interactions";

/** children API'den `[]` gelebiliyor — varlık kontrolü length üzerinden. */
const hasChildren = (item: MenuItem) => Boolean(item.children?.length);

interface MobileNavDetailViewProps {
  menus: MenuItem[];
  activeCategory: MenuItem | null;
  activeCategoryId: number | null;
  activeSubIdx: number;
  onBack: () => void;
  onClose: () => void;
  onTabClick: (id: number) => void;
  onSubClick: (idx: number) => void;
}

const MobileNavDetailView = ({
  menus,
  activeCategory,
  activeCategoryId,
  activeSubIdx,
  onBack,
  onClose,
  onTabClick,
  onSubClick,
}: MobileNavDetailViewProps) => {
  const tabsRef = useRef<HTMLDivElement>(null);

  // Scroll active tab into center using container.scrollTo (scrollIntoView affects ancestor scrolls)
  useEffect(() => {
    if (!tabsRef.current) return;
    const container = tabsRef.current;
    const activeTab = container.querySelector("[data-active='true']") as HTMLElement | null;
    if (activeTab) {
      const offset = activeTab.offsetLeft - container.offsetWidth / 2 + activeTab.offsetWidth / 2;
      container.scrollTo({ left: offset, behavior: "smooth" });
    }
  }, [activeCategoryId]);

  const activeSubcategory = activeCategory?.children?.[activeSubIdx] ?? null;

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-14 bg-white border-b border-gray-100 shrink-0">
        <Button variant="ghost" size="icon-sm" onClick={onBack} className="text-gray-600">
          <ChevronLeft size={22} />
        </Button>
        <h2 className="font-semibold text-base truncate px-2">{activeCategory?.name ?? ""}</h2>
        <Button variant="ghost" size="icon-sm" onClick={onClose} className="text-gray-600">
          <X size={20} />
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white pt-3 pb-2 shrink-0">
        <div className="px-4">
          <Search onPhotoSearchOpen={onClose} />
        </div>
      </div>

      {/* Horizontal Category Tabs */}
      <div className="bg-white shrink-0 overflow-x-auto border-b border-gray-100">
        <div ref={tabsRef} className="flex gap-2 px-4 py-3 w-max">
          {menus.map((cat) => {
            const tabClass = cn(
              "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
              cat.id === activeCategoryId
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600"
            );

            // Alt kategorisi olmayan sekme sekmeye dönüşünce panel boş kalıyordu
            // ("Alt kategori bulunamadı") — bunlar doğrudan sayfasına gider.
            if (!hasChildren(cat) && cat.url) {
              return (
                <Link
                  key={cat.id}
                  href={`/${cat.url}`}
                  data-active={cat.id === activeCategoryId ? "true" : "false"}
                  onClick={() => { trackInteraction("menu", cat.id, "click"); onClose(); }}
                  className={tabClass}
                >
                  {cat.name}
                </Link>
              );
            }

            return (
              <button
                key={cat.id}
                type="button"
                data-active={cat.id === activeCategoryId ? "true" : "false"}
                onClick={() => { trackInteraction("menu", cat.id, "click"); onTabClick(cat.id); }}
                className={tabClass}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Two-column body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: subcategory list */}
        <div className="w-[30%] overflow-y-auto bg-white border-r border-gray-100">
          {(activeCategory?.children ?? []).map((sub, idx) => {
            const subClass = cn(
              "block w-full text-left px-3 py-3 text-xs transition-colors border-l-2",
              idx === activeSubIdx
                ? "font-semibold text-gray-900 bg-gray-100 border-l-gray-800"
                : "text-gray-600 border-l-transparent"
            );

            // Alt kırılımı olmayan başlıkta sağ panelde gösterilecek bir şey yok.
            if (!hasChildren(sub) && sub.url) {
              return (
                <Link
                  key={sub.id}
                  href={`/${sub.url}`}
                  onClick={() => { trackInteraction("menu", sub.id, "click"); onClose(); }}
                  className={subClass}
                >
                  {sub.name}
                </Link>
              );
            }

            return (
              <button
                key={sub.id}
                type="button"
                onClick={() => onSubClick(idx)}
                className={subClass}
              >
                {sub.name}
              </button>
            );
          })}
        </div>

        {/* Right: sub-subcategory tiles (3 columns) */}
        <div className="flex-1 overflow-y-auto bg-[#f1f1f1]">
          {activeSubcategory ? (
            <div className="grid grid-cols-3 gap-2 p-2">
              {/* Tümünü Gör — first tile */}
              <Link
                href={`/${activeCategory?.url ?? activeSubcategory.url}`}
                onClick={onClose}
                className="bg-white rounded-xl overflow-hidden shadow-sm block"
              >
                <div className="relative aspect-square flex items-center justify-center bg-gray-100">
                  <LayoutGrid size={26} className="text-gray-400" />
                </div>
                <p className="text-[10px] font-medium text-center px-1 py-1.5 line-clamp-2 text-gray-700">
                  Tümünü Gör
                </p>
              </Link>

              {(activeSubcategory.children ?? []).map((item) => (
                <Link
                  key={item.id}
                  href={`/${item.url}`}
                  onClick={() => { trackInteraction("menu", item.id, "click"); onClose(); }}
                  className="bg-white rounded-xl overflow-hidden shadow-sm block"
                >
                  <div className="relative aspect-square">
                    <FallbackImage src={item.image_url} alt={item.name} fill className="object-cover" />
                  </div>
                  <p className="text-[10px] font-medium text-center px-1 py-1.5 line-clamp-2">
                    {item.name}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-400 p-6">
              Alt kategori bulunamadı
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileNavDetailView;